import React, { useEffect, useState } from "react";
import {
    SovosBox,
    SovosButton,
    SovosCountryFlag,
    SovosIconButton,
    SovosMenuItem,
    SovosSelect,
    SovosTypography,
} from "mosaic-react";
import { AutoAwesome, Close } from "mosaic-react-icons";

const ChooseEntityCard = ({ onNext, onCancel, i18n, projectModules, availableConfigSets: externalConfigSets, onFetchConfigSets, onFetchEntities, initialConfigSet, initialEntityName, initialFileType, profileId, onSuggestEntity }) => {
    const [configSet, setConfigSet] = useState(initialConfigSet || "");
    const [entityName, setEntityName] = useState(initialEntityName || "");
    const [fileType, setFileType] = useState("");
    const [entities, setEntities] = useState([]);
    const [loadingEntities, setLoadingEntities] = useState(false);
    const [country, setCountry] = useState(initialConfigSet || "");
    const [aiEntitySuggestions, setAiEntitySuggestions] = useState([]);
    const [aiEntityWarnings, setAiEntityWarnings] = useState([]);
    const [loadingAiEntity, setLoadingAiEntity] = useState(false);

    const canProceed = country && fileType;

    useEffect(() => {
        if (!initialConfigSet) return;
        onFetchEntities(initialConfigSet).then((result) => {
            const fetched = result || [];
            setEntities(fetched);
            if (initialFileType || initialEntityName) {
                const match = fetched.find((e) => e.file_type === initialFileType)
                    || fetched.find((e) => e.entity_name === initialEntityName);
                setEntityName(match?.entity_name || initialEntityName || "");
                setFileType(match?.file_type || "");
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialConfigSet, initialEntityName, initialFileType]);

    const handleCountryChange = async (value) => {
        setCountry(value);
        setConfigSet(value);
        setEntityName("");
        setFileType("");
        setEntities([]);
        setAiEntitySuggestions([]);
        setAiEntityWarnings([]);
        if (!value) return;
        setLoadingEntities(true);
        const result = await onFetchEntities(value);
        setEntities(result || []);
        setLoadingEntities(false);
    };

    const handleSuggestEntity = async () => {
        if (!profileId || !configSet || !onSuggestEntity || !entities.length) return;
        setLoadingAiEntity(true);
        setAiEntitySuggestions([]);
        setAiEntityWarnings([]);
        try {
            const result = await onSuggestEntity(profileId, configSet, entities);
            setAiEntitySuggestions(result?.suggestions || []);
            setAiEntityWarnings(result?.warnings || []);
        } finally {
            setLoadingAiEntity(false);
        }
    };

    const handleEntityChange = (val) => {
        const match = entities.find((e) => e.file_type === val)
            || entities.find((e) => e.entity_name === val);
        setEntityName(match?.entity_name || "");
        setFileType(match?.file_type || "");
    };

    const handleNext = () => {
        onNext({ configSet, entityName, fileType, entityLabel: entityName });
    };

    const moduleOptions = externalConfigSets !== null
        ? externalConfigSets
        : projectModules
            .filter((m) => m.name && !["repository", "overview_dashboard", "merge_collisions", "merge_history", "aggregate_org_data", "generic_generator", "custom_templates", "new_navigation"].includes(m.name))
            .map((m) => ({ module: m.name, config_set: m.name, msch_prefix: "", viewMode: m.viewMode }));
    const sourceModule = moduleOptions.filter((module) => module.viewMode === "source" && module.config_set?.startsWith("CSV_"));

    return (
        <SovosBox
            sx={{
                position: "relative",
                bgcolor: "background.paper",
                borderRadius: 2,
                boxShadow: 24,
                p: 3,
                width: 480,
                maxWidth: "90vw",
            }}
        >
            <SovosBox sx={{ position: "absolute", top: 8, right: 8 }}>
                <SovosIconButton size="small" tooltipText="Close" onClick={onCancel}>
                    <Close fontSize="small" />
                </SovosIconButton>
            </SovosBox>

            <SovosBox sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                {onSuggestEntity && <AutoAwesome sx={{ fontSize: 18, color: "primary.main" }} />}
                <SovosTypography variant="h6" fontWeight={600}>
                    {onSuggestEntity ? i18n`Hey, Sovi is helping with your data import` : i18n`Start column mapping by choosing entity`}
                </SovosTypography>
            </SovosBox>
            <SovosTypography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {onSuggestEntity
                    ? i18n`Choose which entity this data should map to. You can preview which fields will be populated from the selected entity.`
                    : i18n`Choose what to map data against (the government schema). We recommend selecting entities based on your column mapping results.`}
            </SovosTypography>

            <SovosSelect
                label="Country"
                value={country || ""}
                onChange={(e) => handleCountryChange(e.target.value)}
                fullWidth
                sx={{ mb: 2.5 }}
                renderValue={(selected) => {
                    const code = selected.replace("CSV_", "");
                    return (
                        <SovosBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <SovosCountryFlag flagHeight={"20px"} countryCode={code} />
                            <SovosTypography variant="body2">{code}</SovosTypography>
                        </SovosBox>
                    );
                }}
            >
                {(sourceModule || []).map((sourceMod) => {
                    const countryCode = sourceMod?.config_set.replace("CSV_", "");
                    return (
                        <SovosMenuItem key={sourceMod?.config_set} value={sourceMod?.config_set}>
                            <SovosBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <SovosCountryFlag
                                    data-testid="country-flag"
                                    flagHeight={"20px"}
                                    countryCode={countryCode}
                                />
                                <SovosTypography variant="body2">{countryCode}</SovosTypography>
                            </SovosBox>
                        </SovosMenuItem>
                    )
                })}
            </SovosSelect>

            <SovosSelect
                label="Entity"
                value={fileType}
                onChange={(e) => handleEntityChange(e.target.value)}
                fullWidth
                disabled={!configSet || loadingEntities}
                sx={{ mb: onSuggestEntity ? 1.5 : 3 }}
            >
                {loadingEntities
                    ? <SovosMenuItem value="" disabled>Loading...</SovosMenuItem>
                    : entities.map((e) => (
                        <SovosMenuItem key={e.file_type} value={e.file_type}>{e.entity_name}</SovosMenuItem>
                    ))
                }
            </SovosSelect>

                    {onSuggestEntity && (
                <SovosBox sx={{ mb: 3 }}>
                    <SovosButton
                        variant="outlined"
                        size="small"
                        fullWidth
                        startIcon={<AutoAwesome sx={{ fontSize: 14 }} />}
                        onClick={handleSuggestEntity}
                        disabled={!configSet || !entities.length || loadingAiEntity}
                    >
                        {loadingAiEntity ? "Suggesting entity..." : "Suggest entity (AI)"}
                    </SovosButton>
                    <SovosTypography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                        Shows ranked guesses for which entity fits your file, from your column headers. Tap a row to use it, or pick Entity yourself.
                    </SovosTypography>

                    {aiEntitySuggestions.length > 0 && (
                        <SovosBox sx={{ mt: 1, border: "1px solid", borderColor: "primary.light", borderRadius: 1, overflow: "hidden" }}>
                            {aiEntitySuggestions.map((s, idx) => {
                                const match = entities.find((e) => e.cdm_entity_name === s.entity_name);
                                const displayLabel = match?.entity_name || s.entity_name;
                                const resolvedFileType = match?.file_type;
                                const isSelected = resolvedFileType && fileType === resolvedFileType;
                                return (
                                <SovosBox
                                    key={s.entity_name}
                                    component="button"
                                    onClick={() => resolvedFileType && handleEntityChange(resolvedFileType)}
                                    sx={{
                                        display: "flex", alignItems: "center", gap: 1, width: "100%",
                                        px: 1.5, py: 0.875, border: "none", fontFamily: "inherit",
                                        borderBottom: idx < aiEntitySuggestions.length - 1 ? "1px solid" : "none",
                                        borderColor: "divider",
                                        bgcolor: isSelected ? "primary.50" : "transparent",
                                        cursor: resolvedFileType ? "pointer" : "default", textAlign: "left",
                                        "&:hover": { bgcolor: isSelected ? "primary.50" : "action.hover" },
                                    }}
                                >
                                    <SovosBox sx={{ flex: 1, minWidth: 0 }}>
                                        <SovosTypography variant="caption" fontWeight={600}>{displayLabel}</SovosTypography>
                                        {s.rationale && (
                                            <SovosTypography variant="caption" color="text.secondary" title={s.rationale} sx={{ display: "-webkit-box", fontSize: 11, WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                                                {s.rationale}
                                            </SovosTypography>
                                        )}
                                    </SovosBox>
                                    <SovosBox sx={{ px: 0.75, py: 0.25, borderRadius: 0.5, bgcolor: "success.light", flexShrink: 0 }}>
                                        <SovosTypography sx={{ fontSize: 10, color: "success.dark", fontWeight: 600 }}>
                                            {Math.round(s.score * 100)}%
                                        </SovosTypography>
                                    </SovosBox>
                                </SovosBox>
                                );
                            })}
                        </SovosBox>
                    )}

                    {aiEntityWarnings.length > 0 && (
                        <SovosBox sx={{ mt: 0.75, p: 1, borderRadius: 1, bgcolor: "warning.light", border: "1px solid", borderColor: "warning.light" }}>
                            {aiEntityWarnings.map((w, i) => (
                                <SovosTypography key={w} variant="caption" color="warning.dark" sx={{ display: "block", mb: i < aiEntityWarnings.length - 1 ? 0.5 : 0 }}>
                                    {w}
                                </SovosTypography>
                            ))}
                        </SovosBox>
                    )}
                </SovosBox>
            )}

            <SovosBox sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                <SovosButton variant="text" onClick={onCancel}>Cancel</SovosButton>
                <SovosButton variant="contained" disabled={!canProceed} onClick={handleNext}>
                    Next
                </SovosButton>
            </SovosBox >
        </SovosBox >
    );
};

export default ChooseEntityCard;
